import { CommonModule, DOCUMENT } from "@angular/common";
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChildren,
    ViewContainerRef,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    viewChild,
} from "@angular/core";
import {
    arrowRotateCwIcon,
    checkIcon,
    chevronDownIcon,
    fullscreenIcon,
    gearIcon,
    layoutSideBySideIcon,
    menuIcon,
    searchIcon,
    thumbDownIcon,
    thumbUpIcon,
    windowIcon,
    xIcon,
} from "@progress/kendo-svg-icons";
import { ButtonsModule } from "@progress/kendo-angular-buttons";
import { KENDO_ICONS } from "@progress/kendo-angular-icons";
import { SliderModule } from "@progress/kendo-angular-inputs";
import { MarkdownModule } from "ngx-markdown";
import { KENDO_CONVERSATIONALUI, SendButtonSettings, SendMessageEvent, User } from "@progress/kendo-angular-conversational-ui";
import { AgentsService } from "../services/agents.service";
import { ActivatedRoute } from "@angular/router";
import { LanguageService } from "../services/language.service";
import { TranslatePipe } from "@ngx-translate/core";
import { ChatbotTranslationsService } from "../services/translations.service";
import { SessionsService } from "../services/sessions.service";
import { guid } from "@progress/kendo-angular-common";
import { FeedbackService } from "../services/feedback.service";
import { IdentityService } from "../services/identity.service";
import { HostBridgeService } from "../services/host-bridge.service";
import { IframeLanguageService } from "../services/iframe-language.service";
import { ChatWidgetStateService } from "../services/chat-widget-state.service";
import { SidebarComponent } from "../sidebar/sidebar.component";
import { SessionSummary } from "../models/session";
import { ChatMessage, ConversationMessage, FeedbackType } from "../models/message";
import { Domain, domainForAgent } from "../models/domain";
import { usesEmbeddedPositioning } from "../models/surface";
import { CHATBOT_CONFIG } from "../config";
import { DomainLoginService } from "../services/domain-login.service";
import { ChatbotView, DEFAULT_VIEW } from "../models/view";
import { AgentSummary, agentDisplayName } from "../models/agent";
import { ATL_CHATBOT_SCOPED_THEME_CSS } from "../theme/theme.generated";

/** `id` of the injected `<style>` carrying the chatbot's own scoped Kendo theme. See
 *  `injectScopedTheme` below. Guards against injecting it twice when several `<avioblue-chatbot>`
 *  instances mount in the same document (e.g. the floating widget and the `/chatbot` route).
 *  Frozen along with the other names carrying the project's current one: changing it while an
 *  older copy is still in the page injects the theme twice instead of once. */
const SCOPED_THEME_STYLE_ID = "atl-chatbot-scoped-theme";

/** localStorage key for the Settings panel's message text size. A purely local preference, and a
 *  frozen name: renaming it resets the size every user chose, and migrates nothing. */
const MESSAGE_FONT_SIZE_KEY = "atl-chatbot:message-font-size";
const DEFAULT_MESSAGE_FONT_SIZE = 16;
const MIN_MESSAGE_FONT_SIZE = 12;
const MAX_MESSAGE_FONT_SIZE = 24;
const MESSAGE_FONT_SIZE_STEP = 2;

function readStoredMessageFontSize(): number {
    const stored = Number(localStorage.getItem(MESSAGE_FONT_SIZE_KEY));
    return Number.isFinite(stored) && stored >= MIN_MESSAGE_FONT_SIZE && stored <= MAX_MESSAGE_FONT_SIZE ? stored : DEFAULT_MESSAGE_FONT_SIZE;
}

/**
 * Resting state of the send button. Deliberately without a `disabled` key: Kendo only falls back
 * to its own rule (disabled while the input is empty, enabled once there is text) when
 * `disabled` is absent (`isPresent(settings.disabled)` wins over everything else). Setting it
 * here, even to `false`, would switch that default off.
 */
const SEND_BUTTON_READY: SendButtonSettings = {};

/**
 * While the agent's answer is pending: disabled and not clickable.
 *
 * This is not a stop button: there is no streaming to interrupt. Kendo's `[loading]` input is
 * unusable for the same reason: it exists for streaming, so it leaves the button *enabled* (see
 * `MessageBoxComponent.isActionButtonDisabled`), swaps in a stop icon and relabels it
 * "Stop generating". The spin animation for `buttonClass` lives in `src/styles.scss`, since the
 * button is rendered inside `kendo-chat`.
 */
const SEND_BUTTON_WAITING: SendButtonSettings = {
    disabled: true,
    themeColor: "primary",
    icon: "arrow-rotate-cw",
    svgIcon: arrowRotateCwIcon,
    buttonClass: "chat-send-loading",
};

/** How stale the conversation left open may be before the chatbot starts a fresh one instead. */
const RESUME_WINDOW_DAYS = 2;

@Component({
    selector: "avioblue-chatbot",
    standalone: true,
    imports: [CommonModule, KENDO_CONVERSATIONALUI, ButtonsModule, KENDO_ICONS, MarkdownModule, SidebarComponent, SliderModule, TranslatePipe],
    templateUrl: "./main.component.html",
    styleUrl: "./main.component.scss",
})
export class AvioblueChatbotComponent implements OnInit, OnDestroy {
    readonly thumbDownIcon = thumbDownIcon;
    readonly thumbUpIcon = thumbUpIcon;
    readonly hamburgerIcon = menuIcon;
    readonly closeIcon = xIcon;
    readonly checkIcon = checkIcon;
    readonly settingsIcon = gearIcon;
    readonly chevronDownIcon = chevronDownIcon;
    readonly searchIcon = searchIcon;
    readonly agentDisplayName = agentDisplayName;

    /** Slider range for the Settings panel's message text size. */
    readonly minMessageFontSize = MIN_MESSAGE_FONT_SIZE;
    readonly maxMessageFontSize = MAX_MESSAGE_FONT_SIZE;
    readonly messageFontSizeStep = MESSAGE_FONT_SIZE_STEP;

    /** Icons for the three "Switch to" options, in the order they're offered. */
    readonly viewIcons: Record<ChatbotView, typeof windowIcon> = {
        floating: windowIcon,
        sidebar: layoutSideBySideIcon,
        fullscreen: fullscreenIcon,
    };
    readonly viewOrder: ChatbotView[] = ["floating", "sidebar", "fullscreen"];

    public readonly user: User = { id: "user" };
    public readonly agent: User = { id: "agent", name: "Agent" };

    /** True from the moment a question leaves the composer until the agent answers or fails. */
    private readonly _waiting = signal(false);

    /**
     * Both backed by `ChatWidgetStateService`, not local fields. See its doc comment. Kept as
     * getters/a getter+setter so every existing read/write in this class and in the template
     * (`chatMessages`, `sessions()`, `sessions.set(...)`) keeps working unchanged.
     */
    private readonly chatState = inject(ChatWidgetStateService);

    public get chatMessages(): ChatMessage[] {
        return this.chatState.chatMessages;
    }
    public set chatMessages(value: ChatMessage[]) {
        this.chatState.chatMessages = value;
    }

    /** The sidebar's list. A signal (see `ChatWidgetStateService`), not a plain field: this
     *  component runs zoneless, so a plain array reassigned from an HTTP
     *  callback would never reach the template. */
    get sessions() {
        return this.chatState.sessions;
    }

    /** The composer's unsent text. See `ChatWidgetStateService.draftMessage` for why
     *  this is lifted out rather than left as `kendo-chat`'s own internal state. Bound two-way to
     *  `<kendo-chat>`'s public `[(inputValue)]` in the template; nothing else in this class reads
     *  or writes it; `sendMessage` clears it once the draft is actually sent. */
    protected get draftMessage(): string {
        return this.chatState.draftMessage;
    }
    protected set draftMessage(value: string) {
        this.chatState.draftMessage = value;
    }

    /**
     * Which of the three views (Floating/Sidebar/Full screen) is active. Which views a host offers
     * and which is the default is decided by each host. This component only starts from whatever
     * `initialView` it's given.
     */
    readonly initialView = input<ChatbotView>(DEFAULT_VIEW);

    /** Hides the "Switch to" dropdown entirely. Needed by the `web` domain, which never offers a view choice. */
    readonly showViewPicker = input(true);

    /**
     * Set directly by a host that imports this component as a library instead of embedding it as a `web` iframe.
     * Takes priority over the `domain` query parameter read in `ngOnInit` below, which stays the only source for the iframe deploy.
     * A host with its own router cannot be expected to carry `?domain=` on every route just because the chatbot is mounted somewhere in its layout.
     */
    readonly domain = input<Domain | null>(null);

    /** Emitted when the top-level close X is pressed in the Floating view. */
    readonly closeRequested = output<void>();

    /**
     * Emitted whenever the View Picker changes the active view. Hosts that imports this component as a library use this
     * to navigate to a dedicated `/chatbot` route when the user picks Full screen, rather than only
     * switching in place like Sidebar/Floating do.
     */
    readonly viewChanged = output<ChatbotView>();

    readonly view = signal<ChatbotView>(DEFAULT_VIEW);
    readonly viewPickerOpen = signal(false);

    readonly settingsOpen = signal(false);

    /**
     * Hides the Agent Picker's dropdown arrow, leaving only the branding button.
     * The default (`true`) is what a standalone dev session sees; each host binds it explicitly,
     * the same pattern already used for `showViewPicker`. It only ever hides a dropdown the
     * domain's own rules already allow, never the other way round.
     */
    readonly showAgentPicker = input(true);

    /**
     * Preselects an agent and starts a fresh, forced-test conversation with it: the
     * "Test" button on the agent configuration page sets this instead of driving the Agent
     * Picker's dropdown directly. It is the same `@Input` mechanism `showAgentPicker`/`domain`
     * already use, so the host needs no parallel communication channel.
     */
    readonly presetAgent = input<string | null>(null);

    readonly agentPickerOpen = signal(false);
    readonly agentSearchQuery = signal("");

    /** Every agent the picker may offer, fetched lazily the first time it's opened. */
    private readonly agents = signal<AgentSummary[] | null>(null);

    /**
     * The current agent, minus itself and filtered by the search box. Excluding the active agent is
     * what lets "switch back to the domain's own agent" reuse this same list (see
     * `AgentsService.isTestSession`): once a sibling or sub-agent is selected, the domain's own
     * agent reappears here as a way back.
     */
    readonly filteredAgents = computed(() => {
        const query = this.agentSearchQuery().trim().toLowerCase();
        const current = this.agentsService.agentName;
        return (this.agents() ?? []).filter((agent) => agent.name !== current && (query === "" || agentDisplayName(agent.name).toLowerCase().includes(query)));
    });

    /**
     * The Settings panel's only control: the font size of chat **messages** only, not the
     * composer, sidebar, or the rest of the chatbot UI. Applied via a CSS custom property
     * scoped to `.message-content` (see the template and stylesheet), so it can't leak elsewhere.
     * Persisted so it survives closing and reopening the whole chatbot.
     */
    readonly messageFontSize = signal(readStoredMessageFontSize());

    /**
     * The session-history drawer's open/closed state. Never persisted and
     * never restored open: it is opened only from the hamburger icon and is the same overlay
     * regardless of which of the three View Picker views is active, so there is no steady "docked"
     * state worth remembering across reloads. See the `expanded` doc comment on `SidebarComponent`.
     */
    readonly sidebarOpen = signal(false);

    protected readonly identity = inject(IdentityService);
    private readonly hostBridge = inject(HostBridgeService);
    private readonly iframeLanguage = inject(IframeLanguageService);
    protected readonly domainLogin = inject(DomainLoginService);
    private readonly config = inject(CHATBOT_CONFIG);
    protected readonly agentsService = inject(AgentsService);
    private readonly route = inject(ActivatedRoute);
    private readonly languageService = inject(LanguageService);
    private readonly chatbotTranslations = inject(ChatbotTranslationsService);
    private readonly sessionsService = inject(SessionsService);
    private readonly feedbackService = inject(FeedbackService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly document = inject(DOCUMENT);

    @ViewChildren("messageElement", { read: ElementRef }) messageList!: QueryList<ElementRef>;

    /**
     * A `ViewContainerRef` anchored inside this component's own root, so Kendo popups that would
     * otherwise portal to `document.body` by default (only the sidebar's row `kendo-contextmenu`)
     * can be told to mount here instead. Outside this scope they would fall outside
     * `.atl-chatbot-scope` and lose the chatbot's own theme entirely (see `scripts/build-theme.mjs`).
     */
    protected readonly appendTarget = viewChild("appendTargetRef", { read: ViewContainerRef });

    constructor() {
        this.injectScopedTheme();
    }

    /**
     * Injects the chatbot's own pre-scoped Kendo theme into `<head>` once per document, regardless
     * of how many `<avioblue-chatbot>` instances mount. This is what makes the chatbot
     * look identical in every host, whichever theme that host loads for itself or none at all,
     * without requiring a single line of host configuration (`angular.json`,
     * `styles.scss`, or otherwise). See `scripts/build-theme.mjs` for how the CSS is generated
     * and why the `@scope` at-rule is what keeps it from leaking either direction.
     */
    private injectScopedTheme(): void {
        if (this.document.getElementById(SCOPED_THEME_STYLE_ID)) {
            return;
        }
        const style = this.document.createElement("style");
        style.id = SCOPED_THEME_STYLE_ID;
        style.textContent = ATL_CHATBOT_SCOPED_THEME_CSS;
        this.document.head.appendChild(style);
    }

    /**
     * Applies `initialView` as soon as it is actually available. A plain constructor-time
     * `this.view.set(this.initialView())` reads the signal before Angular has applied the host's
     * bound value to it, silently capturing the default (`floating`) instead. This `effect` reruns
     * once the real value lands. It never fights a later manual `selectView()`: `initialView` is set
     * once by the host template and never rebound, so the effect does not re-run after that.
     */
    private readonly applyInitialView = effect(() => this.view.set(this.initialView()));

    /**
     * Mirrors `applyInitialView`'s timing fix: reads `presetAgent` from an `effect`, not the
     * constructor, so it sees the host's actually-bound value. Forces `isTestSession` via
     * `{ forceTest: true }`, see `AgentsService.selectAgent`, because the tested agent may be the
     * domain's own, which the plain "different from default" heuristic alone would miss.
     *
     * Compares against `chatState.lastAppliedPresetAgent` (not a component field, see
     * `ChatWidgetStateService`) so a remount with the same preset, e.g. switching Floating ↔
     * Sidebar while testing an agent, doesn't restart the test conversation.
     */
    private readonly applyPresetAgent = effect(() => {
        const preset = this.presetAgent();
        if (preset && preset !== this.chatState.lastAppliedPresetAgent) {
            this.chatState.lastAppliedPresetAgent = preset;
            this.agentsService.selectAgent(preset, { forceTest: true });
            // Same rule as picking it from the dropdown: an agent that answers for another
            // declared domain is tried out as one of its users, so its sign in is asked for here
            // too, rather than only on the path a tester happens to take.
            void this.applyIdentityForAgent(preset).then(() => this.startNewChat());
        }
    });

    /**
     * Rewrites the opening line when the language settles.
     *
     * The conversation opens as soon as the widget does, which can be before the host or the
     * embedding page has said which language this is: the line would otherwise stay in whichever
     * language happened to be current at that instant, alone among everything around it.
     */
    private readonly translateGreeting = effect(() => {
        this.languageService.currentLanguage();
        const first = this.chatMessages[0];
        if (!first?.greeting) {
            return;
        }
        first.text = this.chatbotTranslations.instant("chatbot.firstMessage");
        this.cdr.detectChanges();
    });

    ngOnInit(): void {
        this.route.queryParams.subscribe((params) => {
            // The host declares which audience this instance serves. Everything domain-dependent
            // (identity header, agent, which controls are offered) follows from it, and a domain
            // the configuration does not declare throws here rather than resolving to anything.
            // The `domain` input, when set, wins over the query parameter. See its own doc comment.
            this.identity.setDomain(this.domain() ?? params["domain"]);
            this.hostBridge.connect();
            // Across a frame boundary there is no shared translation service to read the language
            // from, so the embedding page tells the widget instead. Harmless where there is no
            // frame: it connects nothing.
            this.iframeLanguage.connect();

            const lang = params["lang"];
            if (lang) {
                this.languageService.applyRequestedLanguage(lang);
            }
            if (Boolean(params["test"]) && window.innerWidth > 1280) {
                const container = document.getElementsByClassName("chat-container")[0] as HTMLElement;
                if (container) {
                    container.style.width = "50%";
                    container.style.float = "right";
                }
            }
        });

        // Guarded so a remount within the same app lifetime (Floating ↔ Sidebar switch, minimize then
        // reopen: both destroy and recreate this component, see `ChatWidgetStateService`) picks up
        // the conversation already in `chatState` instead of re-fetching and resetting to the
        // greeting message.
        if (!this.chatState.initialized) {
            this.chatState.initialized = true;
            setTimeout(() => this.initializeSessions(), 1);
        }
    }

    ngOnDestroy(): void {
        this.hostBridge.disconnect();
        this.iframeLanguage.disconnect();
    }

    /**
     * Whether the sidebar offers rename and delete. Follows the domain the host mounted this
     * widget for: a domain whose identity the backend cannot verify is refused those calls
     * anyway, so offering them would promise something the server will not do.
     *
     * The host's own domain, not the one a test identity is currently speaking as: signing in as
     * another domain's user changes who the backend thinks is asking, never which controls the
     * tester has in front of them.
     */
    protected get canManageSessions(): boolean {
        return this.identity.hostRules()?.allowsSessionManagement ?? false;
    }

    /** Whether the Agent Picker's dropdown arrow shows at all. The input kill switch, gated again
     *  by the host domain's own rule: only a domain whose credential may read the protected agent
     *  listing can draw the dropdown from it. */
    protected get canPickAgent(): boolean {
        return this.showAgentPicker() && (this.identity.hostRules()?.showsAgentPicker ?? false);
    }

    /**
     * The host's own primary colour, set on this widget's root so the theme derives every shade it
     * uses from it: the slider, the buttons, and whatever the host draws behind the closed bubble.
     */
    protected get primaryColor(): string {
        return this.config.primaryColor;
    }

    /**
     * The host's base text size, set on this widget's root where it overrides the theme's own
     * declaration. Absent when the host names none, and the theme's `1rem` stands.
     */
    protected get fontSize(): string | null {
        return this.config.fontSize ?? null;
    }

    /** Whether this instance must position itself on screen (fixed corner bubble / docked panel)
     *  rather than simply filling its containing element. A property of how the widget is mounted,
     *  not of the domain: see `usesEmbeddedPositioning`. */
    protected get embeddedPositioning(): boolean {
        return usesEmbeddedPositioning(this.config.surface);
    }

    toggleViewPicker(): void {
        this.viewPickerOpen.update((open) => !open);
    }

    selectView(next: ChatbotView): void {
        // Closes the session-history drawer on every switch: it's an overlay in every view, so
        // leaving it open across a resize/reposition would show it stacked oddly on
        // top of the widget while there's nothing view-level visible yet to close it with.
        this.sidebarOpen.set(false);
        this.view.set(next);
        this.viewPickerOpen.set(false);
        this.viewChanged.emit(next);
    }

    toggleSettings(): void {
        this.settingsOpen.update((open) => !open);
    }

    /**
     * Signs out of the identity borrowed for this test and asks for it again, so the tester can
     * carry on as another of that domain's users, or with different details.
     *
     * One control for both needs: cancelling the dialog leaves the conversation on the host's own
     * user, which is the plain sign out, and filling it in again switches user without having to
     * go back through the agent list.
     */
    protected async changeTestUser(): Promise<void> {
        this.settingsOpen.set(false);
        const agent = this.agentsService.agentName;
        this.domainLogin.stop();
        if (agent) {
            await this.applyIdentityForAgent(agent);
        }
        this.startNewChat();
    }

    onMessageFontSizeChange(value: number): void {
        this.messageFontSize.set(value);
    }

    /** Opens the Agent Picker, fetching the list on first use. It rarely changes mid-session and
     *  every subsequent open reuses what's already loaded. */
    toggleAgentPicker(): void {
        const opening = !this.agentPickerOpen();
        this.agentPickerOpen.set(opening);
        if (opening && this.agents() === null) {
            this.agentsService.list().subscribe({
                next: (agents) => this.agents.set(agents),
                error: (err) => {
                    console.error("Failed to load agents", err);
                    this.agents.set([]);
                },
            });
        }
    }

    /**
     * Switches to a different agent and starts a **new** conversation with it, never continuing
     * the one on screen, which would mix messages from two agents into a single session.
     * `AgentsService.query` picks up the test flag on its own from `isTestSession`.
     */
    protected async selectAgent(agent: AgentSummary): Promise<void> {
        this.agentPickerOpen.set(false);
        this.agentSearchQuery.set("");
        await this.applyIdentityForAgent(agent.name);
        this.agentsService.selectAgent(agent.name);
        this.startNewChat();
    }

    /**
     * Puts the conversation on whichever identity the agent being tried out calls for.
     *
     * An agent that answers for another declared domain is only worth trying as one of that
     * domain's users, so its credentials are asked for here. Everything else, the domain's own
     * agent and the agents no domain claims, goes back to the host's own user: an identity
     * borrowed for the previous agent has no business outliving it.
     *
     * A cancelled or failed sign in leaves the agent selected and the host's identity in place,
     * which the composer then refuses to send from: that agent answers for another domain and
     * may only be asked as one of its users. The notice in the composer offers the sign in
     * again.
     */
    private async applyIdentityForAgent(agent: string): Promise<void> {
        const target = domainForAgent(this.config.domains, agent);
        const foreign = target && target.name !== this.identity.hostDomain();
        if (!foreign) {
            this.domainLogin.stop();
            return;
        }

        if (this.identity.testDomain() === target.name) {
            return;
        }

        // A domain with no credential has nothing to sign into: its identity is a UUID a visitor
        // mints, so taking it on is the whole exchange. This is how the agent that answers
        // visitors is tried out, as one.
        if (target.rules.identity === "anonymous") {
            this.identity.startTestIdentity(target.name);
            return;
        }

        if (target.rules.login) {
            await this.domainLogin.signIn(target.name, target.rules.login);
        }
    }

    /**
     * The top-level close X. Always hides the whole chatbot, whichever view is active. It used to
     * step Sidebar back to Floating instead of closing, but that
     * silently discarded the user's chosen view: reopening from the host's navbar button must come
     * back in the *same* view and with the conversation intact, not reset to Floating. Closing
     * itself never touches `view`. The host remembers it on its own side, persisted, and
     * `ChatWidgetStateService` keeps the conversation alive across the resulting unmount, so a
     * reopen restores both. Full screen never shows this button at all. See the template's `@if`.
     */
    onCloseClick(): void {
        this.closeRequested.emit();
    }

    public initializeSessions() {
        const userId = this.identity.userId;
        if (!userId) {
            this.sendFirstMessage();
            return;
        }

        this.sessionsService.getSessionSummaries(userId).subscribe({
            next: (sessions) => {
                this.sessions.set(sessions);

                const clientToken = this.identity.clientToken;
                const active = clientToken ? sessions.find((s) => s.clientToken === clientToken) : undefined;

                if (!active) {
                    this.sendFirstMessage();
                    return;
                }

                // A conversation left untouched for days is history, not something to drop the user back
                // into. It stays in the sidebar; only the auto-resume is skipped.
                const staleAfter = new Date();
                staleAfter.setDate(staleAfter.getDate() - RESUME_WINDOW_DAYS);

                if (new Date(active.lastMessageAt) < staleAfter) {
                    this.identity.clearSession();
                    this.sendFirstMessage();
                    return;
                }

                this.loadConversation(userId, active.clientToken);
            },
            error: (err) => {
                console.error("Failed to load sessions", err);
                this.sendFirstMessage();
            },
        });
    }

    /** Re-reads the sidebar list from the server. Cheap: it carries titles and dates, no messages. */
    private refreshSessions(): void {
        const userId = this.identity.userId;
        if (!userId) {
            return;
        }
        this.sessionsService.getSessionSummaries(userId).subscribe({
            next: (sessions) => this.sessions.set(sessions),
            error: (err) => console.error("Failed to refresh sessions", err),
        });
    }

    public sendFirstMessage() {
        const greeting = this.chatbotTranslations.instant("chatbot.firstMessage");
        this.chatMessages = [{ id: guid(), author: this.agent, text: greeting, timestamp: new Date(), typing: true, greeting: true }];
        this.cdr.detectChanges();
        setTimeout(() => {
            this.chatMessages[0].typing = false;
            this.cdr.detectChanges();
        }, 1000);
    }

    /** Opens a conversation picked from the sidebar. */
    public openSession(session: SessionSummary): void {
        const userId = this.identity.userId;
        if (!userId) {
            return;
        }
        this.identity.setSession(session.clientToken, userId);
        this.loadConversation(userId, session.clientToken);
    }

    /**
     * Pulls one conversation's messages.
     *
     * Fetched on demand rather than kept in the sidebar's rows: the list endpoint deliberately
     * carries no transcripts, so this is the call that pays for the one conversation being read.
     */
    private loadConversation(userId: string, clientToken: string): void {
        this.sessionsService.getClientSession(userId, clientToken).subscribe({
            next: (conversation) => this.loadSession(conversation ?? []),
            error: (err) => {
                console.error("Failed to load conversation", err);
                this.sendFirstMessage();
            },
        });
    }

    /** Clears the open conversation. The history itself is untouched. */
    public startNewChat(): void {
        this.identity.clearSession();
        this.chatMessages = [];
        this.cdr.detectChanges();
        this.sendFirstMessage();
    }

    public renameSession(event: { id: string; title: string }): void {
        this.sessionsService.renameSession(event.id, event.title).subscribe({
            // The stored title, not the one that was typed: the backend trims it.
            next: (renamed) => this.sessions.update((sessions) => sessions.map((s) => (s.id === renamed.id ? { ...s, title: renamed.title } : s))),
            error: (err) => console.error("Failed to rename session", err),
        });
    }

    public deleteSession(sessionId: string): void {
        const deleted = this.sessions().find((s) => s.id === sessionId);

        this.sessionsService.deleteSession(sessionId).subscribe({
            next: () => {
                this.sessions.update((sessions) => sessions.filter((s) => s.id !== sessionId));

                // Deleting the conversation on screen would otherwise leave it readable, and still
                // resumable, after it has left the history.
                if (deleted && deleted.clientToken === this.identity.clientToken) {
                    this.startNewChat();
                }
            },
            error: (err) => console.error("Failed to delete session", err),
        });
    }

    public loadSession(session: ConversationMessage[]) {
        const loadedChatMessages: ChatMessage[] = session.map((m) => ({
            ...m,
            author: m.author === "user" ? this.user : this.agent,
            // The API returns UTC. Older rows come back without the `Z`, and `new Date()` would then
            // read them as local time, so the suffix is added when it is missing.
            timestamp: new Date(String(m.timestamp).endsWith("Z") ? String(m.timestamp) : String(m.timestamp) + "Z"),
        }));

        const greeting = this.chatbotTranslations.instant("chatbot.firstMessage");
        this.chatMessages = [{ id: guid(), author: this.agent, text: greeting, timestamp: new Date(), greeting: true }, ...loadedChatMessages];
        this.cdr.detectChanges();
    }

    public sendMessage(message: SendMessageEvent): void {
        if (!message.message.text) {
            return;
        }

        // Kendo clears its own internal composer text on send via a plain field assignment, not
        // through a setter that emits `inputValueChange`, so our two-way-bound `draftMessage`
        // would otherwise keep showing the just-sent text as "unsent" the next time the widget closes
        // and reopens, even though the composer itself is visibly empty.
        this.draftMessage = "";

        const userMessage: ChatMessage = {
            id: guid(),
            author: this.user,
            text: message.message.text,
            timestamp: new Date(),
        };
        this.chatMessages = [...this.chatMessages, userMessage];

        const agentResponse: ChatMessage = {
            id: "",
            author: this.agent,
            text: "",
            timestamp: new Date(),
            typing: true,
        };

        // Set before the delay below, so the send button reports the wait for the whole time the user
        // is actually waiting, not just from when the request leaves.
        this._waiting.set(true);

        setTimeout(() => {
            this.chatMessages = [...this.chatMessages, agentResponse];
            // The app runs zoneless (zone.js is in the polyfills but no zone change detection is
            // provided), so an update from a timer callback reaches no template on its own, hence the
            // explicit detection here, as in every other async branch of this component. Without it the
            // typing bubble never reaches Kendo's `[messages]` input.
            this.cdr.detectChanges();
            this.agentsService.query(message.message.text ?? "").subscribe({
                next: (response) => {
                    const isNewSession = !this.sessions().some((s) => s.clientToken === response.clientToken);

                    const agentResponseRes = response.messages.filter((m) => m.author === "agent")[0];

                    agentResponse.id = agentResponseRes.id;
                    agentResponse.text = agentResponseRes.text;
                    agentResponse.timestamp = new Date(agentResponseRes.timestamp);
                    agentResponse.typing = false;
                    this._waiting.set(false);

                    this.chatMessages = [...this.chatMessages];
                    this.cdr.detectChanges();
                    this.scrollToNewestMessage();

                    // The title of a brand-new conversation is derived from this very first question, and
                    // only the server knows where it cut it, so the row is fetched rather than guessed.
                    if (isNewSession) {
                        this.refreshSessions();
                    }
                },
                error: (err) => {
                    // The limit is enforced conversationally by the agent, so an error here is a genuine
                    // failure: replace the typing bubble rather than leaving it spinning forever.
                    console.error("Query failed", err);
                    agentResponse.text = this.chatbotTranslations.instant("chatbot.errorGeneric");
                    agentResponse.typing = false;
                    this._waiting.set(false);
                    this.chatMessages = [...this.chatMessages];
                    this.cdr.detectChanges();
                },
            });
        }, 1000);
    }

    public sendFeedback(messageId: string, type: FeedbackType): void {
        this.feedbackService.sendFeedback(messageId, type).subscribe({
            next: () => {
                const message = this.chatMessages.find((m) => m.id === messageId);
                if (message) {
                    message.feedbackType = type;
                    this.cdr.detectChanges();
                }
            },
            error: (err) => console.log(err),
        });
    }

    /**
     * Whether the composer accepts input. A blocked visitor must convert first, a domain with a
     * credential waits for it before it can query at all, and an agent that answers for another
     * domain cannot be asked anything until the tester is signed in as one of its users.
     */
    protected get canSend(): boolean {
        return !this.identity.awaitingCredentials() && !this.domainLogin.busy() && !this.agentsService.signInRequired();
    }

    /**
     * Which wording the two identity controls use, since a domain with no credential is not
     * signed into: it is simply taken on, and taken on again as somebody nobody has seen.
     */
    protected get identityNeedsCredential(): boolean {
        const domain = this.agentsService.signInRequired() ?? this.identity.testDomain();
        return domain ? this.config.domains[domain]?.identity === "bearer" : true;
    }

    protected get takeOnIdentityKey(): string {
        return this.identityNeedsCredential ? "chatbot.signIn.action" : "chatbot.signIn.continueAs";
    }

    protected get changeIdentityKey(): string {
        return this.identityNeedsCredential ? "chatbot.signIn.changeUser" : "chatbot.signIn.newIdentity";
    }

    protected get changeIdentityHintKey(): string {
        return this.identityNeedsCredential ? "chatbot.signIn.changeUserHint" : "chatbot.signIn.newIdentityHint";
    }

    /**
     * Asks for the credentials of the domain the current agent answers for, from the notice that
     * replaced the composer.
     *
     * Reached after a sign in was cancelled or failed: the agent stays selected, the composer
     * stays closed, and this is the way back in without going through the agent list again.
     */
    protected async takeOnIdentityForCurrentAgent(): Promise<void> {
        const agent = this.agentsService.agentName;
        if (agent) {
            await this.applyIdentityForAgent(agent);
        }
    }

    /**
     * The three states of the send button: disabled on empty input (Kendo's own default), enabled
     * with an arrow once there is text, and a disabled spinner while the answer is pending. Returns
     * one of two stable constants because `ChatComponent.ngOnChanges` compares the input by
     * identity: a fresh object literal per change detection would re-push settings on every cycle.
     */
    protected get sendButton(): SendButtonSettings {
        return this._waiting() ? SEND_BUTTON_WAITING : SEND_BUTTON_READY;
    }

    scrollToNewestMessage(): void {
        setTimeout(() => {
            if (this.messageList && this.messageList.length > 0) {
                const newestMessageElement = this.messageList.last.nativeElement;
                newestMessageElement.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }, 10);
    }
}
